import React, { useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useZodForm } from "@/lib/forms/useZodForm";
import { Button } from "../ui/button";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Form,
} from "../ui/form";
import { z } from "zod";
import { errorToast } from "@/lib/utils/ToastUtils";
import { Metric } from "./metricsColumns";
import { trpc } from "@/lib/trpc/client";
import {
  patchKeyedListCache,
  removeFromListBy,
  upsertInListBy,
} from "@trainrr/api-client";
import { InputDatePicker } from "@/components/ui/input-date-picker";
import { useWeekStartsOn } from "@/lib/dates/useWeekStartsOn";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";

export interface MetricLog {
  value: number;
  logged_at: string;
}

interface EditMetricLogDialogProps {
  clientId: string;
  metric: Metric;
  log?: MetricLog;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formSchema = z.object({
  value: z.coerce.number().finite(),
  date: z.string().min(1),
});

const EditMetricLogDialog: React.FC<EditMetricLogDialogProps> = ({
  open,
  onOpenChange,
  log,
  metric,
  clientId,
}) => {
  const weekStartsOn = useWeekStartsOn();
  const metricName = metric.name || "Metric";
  const unit = metric.unit?.trim() ?? "";
  const today = toLocalIsoDate(new Date());

  const listInput = {
    clientId,
    metricId: metric.id,
  };
  const utils = trpc.useUtils();

  const patchLogCaches = () => {
    void utils.metrics.listForClient.invalidate({ clientId });
    void utils.metrics.listRecentLogs.invalidate();
    void utils.metrics.chart.invalidate(listInput);
  };

  const saveLog = trpc.metrics.log.useMutation({
    onSuccess: (saved) => {
      patchKeyedListCache(utils.metrics.listLogs, listInput, (old) => {
        const withoutOld = log?.logged_at
          ? removeFromListBy(old, "logged_at", log.logged_at)
          : old;
        return upsertInListBy(withoutOld, saved, "logged_at");
      });
      patchLogCaches();
      onOpenChange(false);
    },
    onError: (error) => {
      errorToast(error, "Error updating metric log");
    },
  });

  const deleteLog = trpc.metrics.deleteLog.useMutation({
    onSuccess: () => {
      if (log?.logged_at) {
        patchKeyedListCache(utils.metrics.listLogs, listInput, (old) =>
          removeFromListBy(old, "logged_at", log.logged_at),
        );
      }
      patchLogCaches();
      onOpenChange(false);
    },
    onError: (error) => {
      errorToast(error, "Error deleting metric log");
    },
  });

  const defaultValues = useCallback(() => {
    return {
      value: log?.value ?? 0,
      date: log?.logged_at
        ? toLocalIsoDate(new Date(log.logged_at))
        : toLocalIsoDate(new Date()),
    };
  }, [log]);

  const form = useZodForm(formSchema, {
    defaultValues: defaultValues(),
  });

  useEffect(() => {
    form.reset(defaultValues());
  }, [log, form, defaultValues, open]);

  const onSubmit = (values: z.output<typeof formSchema>) => {
    const date = values.date.slice(0, 10);
    const existingDate = log?.logged_at
      ? toLocalIsoDate(new Date(log.logged_at))
      : undefined;
    const loggedAt =
      log && existingDate === date
        ? log.logged_at
        : new Date(`${date}T00:00:00`).toISOString();

    saveLog.mutate({
      clientId,
      metricId: metric.id,
      value: values.value,
      loggedAt,
      oldLoggedAt: log?.logged_at,
    });
  };

  const isPending = saveLog.isPending || deleteLog.isPending;
  const targetLabel =
    metric.daily_target != null
      ? `Daily target ${metric.daily_target}${unit ? ` ${unit}` : ""}`
      : null;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      analyticsName="edit_metric_log_dialog"
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {log ? "Update log" : `Log ${metricName.toLowerCase()}`}
          </DialogTitle>
          {targetLabel && (
            <DialogDescription>{targetLabel}</DialogDescription>
          )}
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Value</FormLabel>
                  <FormControl>
                    <InputGroup>
                      <InputGroupInput
                        placeholder={unit || metricName}
                        type="number"
                        step="any"
                        {...field}
                      />
                      {unit && (
                        <InputGroupAddon align="inline-end">
                          <InputGroupText>{unit}</InputGroupText>
                        </InputGroupAddon>
                      )}
                    </InputGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <InputDatePicker
                      key={field.value}
                      id="metric-log-date"
                      label="Date"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select date"
                      maxDate={today}
                      weekStartsOn={weekStartsOn}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className={log ? "sm:justify-between" : undefined}>
              {log && (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={isPending}
                  onClick={() => {
                    deleteLog.mutate({
                      clientId,
                      metricId: metric.id,
                      loggedAt: log.logged_at,
                    });
                  }}
                >
                  Delete
                </Button>
              )}
              <Button type="submit" disabled={isPending}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

function toLocalIsoDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default EditMetricLogDialog;
